import type { GrowthBook } from '@growthbook/growthbook'
import type { TRemoteConfigVariantData } from 'helpers/getRemoteConfigVariantData'
import { type GrowthbookKey } from 'root-constants'

const findExperimentFeatureKey = (
  growthbook: GrowthBook,
  experimentKey: string,
): string | null => {
  const features = growthbook.getFeatures()

  if (features[experimentKey]) return experimentKey

  const hostFeature = Object.entries(features).find(([, definition]) =>
    definition.rules?.some((rule) => rule.key === experimentKey),
  )

  return hostFeature?.[0] || null
}

const isOffValue = (value: string): boolean =>
  value?.trim()?.toLowerCase() === 'off'

export const getGrowthbookVariantData = ({
  growthbook,
  growthbookKey,
}: {
  growthbook: GrowthBook | null
  growthbookKey?: GrowthbookKey | GrowthbookKey[] | null
}): TRemoteConfigVariantData => {
  if (!growthbook || !growthbookKey) return {}

  const keys = Array.isArray(growthbookKey) ? growthbookKey : [growthbookKey]

  return Object.fromEntries(
    keys.map((key) => {
      const featureKey = findExperimentFeatureKey(growthbook, key)

      if (!featureKey) {
        return [key, { value: '', sendAnalytics: false }]
      }

      const { source, value, experiment, experimentResult } =
        growthbook.evalFeature(featureKey)
      const variantValue =
        source === 'experiment' && experiment?.key === key
          ? experimentResult?.inExperiment
            ? experimentResult.name || String(value ?? '')
            : ''
          : featureKey === key && value != null
            ? String(value)
            : ''

      return [
        key,
        {
          value: variantValue,
          sendAnalytics: Boolean(variantValue) && !isOffValue(variantValue),
        },
      ]
    }),
  )
}
